import { Link } from "react-router-dom";
import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, GraduationCap, Users2, Trophy, Rocket, CheckCircle2, Sparkles, Award, Globe2, Beaker, Target, ChevronLeft, ChevronRight } from "lucide-react";
import { PageHero, CTASection } from "@/components/Bits";
import { SectionLabel } from "@/components/SectionLabel";
import { Reveal } from "@/components/Reveal";
import { IMG, EXAM_BADGES } from "@/data/content";

const NTP_LIVE_SLIDES = [
  { src: "/ntp-live-1.jpg", label: "Grade 8 Chemistry · Chemical Bonding — Level 1 concept-building problems with a live class test" },
  { src: "/ntp-live-2.jpg", label: "Grade 8 Chemistry · Chemical Bonding — forming sodium chloride, taught concept-first" },
  { src: "/ntp-live-3.jpg", label: "Grade 11 Chemistry · Atomic Structure (Advanced) — Bohr orbit as an electron standing wave" },
  { src: "/ntp-live-4.jpg", label: "Grade 11 Chemistry · Atomic Structure — Heisenberg bound: momentum vs position uncertainty" },
  { src: "/ntp-live-5.jpg", label: "Grade 11 Chemistry · Atomic Structure — step-by-step uncertainty solution with class test" },
];

function NtpLiveSlider() {
  const [i, setI] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setI((v) => (v + 1) % NTP_LIVE_SLIDES.length), 5000);
    return () => clearInterval(id);
  }, []);
  const go = (d) => setI((v) => (v + d + NTP_LIVE_SLIDES.length) % NTP_LIVE_SLIDES.length);
  return (
    <div className="rounded-2xl border dk-border dk-panel overflow-hidden" data-testid="ntp-live-slider">
      <div className="px-6 md:px-8 py-4 border-b dk-border flex items-center justify-between gap-4 flex-wrap">
        <div>
          <div className="font-mono-label text-[11px] tracking-label uppercase text-c-amber">The NTP Platform · Live Classes in Action</div>
          <p className="dk-text-muted text-sm mt-1">Real screenshots from live evening sessions on learn.tce-pbl.uk</p>
        </div>
        <div className="flex items-center gap-2">
          <button type="button" onClick={() => go(-1)} aria-label="Previous slide" data-testid="ntp-slider-prev" className="w-9 h-9 rounded-full border dk-border dk-text flex items-center justify-center hover:bg-accent transition-colors">
            <ChevronLeft className="w-4 h-4" />
          </button>
          <button type="button" onClick={() => go(1)} aria-label="Next slide" data-testid="ntp-slider-next" className="w-9 h-9 rounded-full border dk-border dk-text flex items-center justify-center hover:bg-accent transition-colors">
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
      <div className="relative bg-black" style={{ aspectRatio: "1283 / 710" }}>
        <AnimatePresence mode="wait">
          <motion.img
            key={i}
            src={NTP_LIVE_SLIDES[i].src}
            alt={NTP_LIVE_SLIDES[i].label}
            initial={{ opacity: 0, scale: 1.02 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.6 }}
            className="absolute inset-0 w-full h-full object-contain"
          />
        </AnimatePresence>
      </div>
      <div className="px-6 md:px-8 py-4 border-t dk-border flex items-center justify-between gap-4">
        <p className="dk-text-soft text-sm leading-snug">{NTP_LIVE_SLIDES[i].label}</p>
        <div className="flex items-center gap-2 shrink-0">
          {NTP_LIVE_SLIDES.map((_, d) => (
            <button
              key={d}
              type="button"
              aria-label={`Go to slide ${d + 1}`}
              onClick={() => setI(d)}
              className={`h-2 rounded-full transition-all ${d === i ? "w-6 bg-c-amber" : "w-2 bg-white/40 hover:bg-white/60"}`}
              style={d === i ? {} : { backgroundColor: "rgba(255,255,255,0.3)" }}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

const PILLARS = [
  { n: "01", t: "Advanced problem solving & research mentoring", d: "Extending classroom learning into structured research projects and one-to-one mentoring with experienced scientists and educators." },
  { n: "02", t: "Innovation projects & international competitions", d: "Students build real innovation projects and compete on the world stage through olympiads and research fairs." },
  { n: "03", t: "Entrepreneurship awareness",                        d: "Turning ideas into ventures — helping students think like founders, not just candidates." },
  { n: "04", t: "University admissions",                              d: "Profile building, application strategy and guidance for top National & International universities — including the UK and USA — with support for up to 100% scholarships." },
];

const NTP_EDGES = [
  { icon: Users2,       hex: "#2E6BB0", t: "Expert Mentorship", d: "Guided by scientists and educators from TIFR, IITs, international research labs and top global universities." },
  { icon: Target,       hex: "#F0A831", t: "Personalised Strategy", d: "One-to-one preparation plan mapped to each student's competitions, admissions and research goals." },
  { icon: Globe2,       hex: "#1E9E8A", t: "Global Exposure",       d: "Access to BPhO, AMC, AP, BIOlympiad, PhysicsBowl and other international academic challenges." },
  { icon: Trophy,       hex: "#8B5CF6", t: "Proven Results",        d: "Consistent olympiad medals, world-university admissions and scholarships year after year." },
  { icon: Rocket,       hex: "#E14C7A", t: "Future Leaders",        d: "Building problem solvers, innovators, researchers and entrepreneurs for a global tomorrow." },
];

const PHILOSOPHY = [
  "Strong conceptual learning in Physics, Chemistry, Biology and Mathematics",
  "Problem-based learning and structured research projects",
  "International academic competitions from Grade 6 onward",
  "Meaningful research experience with mentor scientists",
  "Leadership and communication skills",
  "Continuous mentoring and systematic profile development",
];

const HIGHLIGHTS = [
  { v: "100%",  l: "Results in Cambridge AS & A Level" },
  { v: "OCLA",  l: "Cambridge Highest OCLA Award in IGCSE Board" },
  { v: "100%",  l: "Students received admission offers for undergraduate programs" },
  { v: "100%",  l: "Students received scholarships in the last two years" },
  { v: "100%",  l: "Scholarship for 3 students at USA & UK top universities" },
  { v: "100%",  l: "International medals in BPhO (Senior) by Grade 11" },
  { v: "100%",  l: "Global gold in BPhO (Junior) by high-school students" },
  { v: "8/13",  l: "Outstanding Achievement Award — AMC" },
  { v: "AP",    l: "AP Award from AMECA in Physics" },
  { v: "G9",    l: "BIOlympiad international medal by Grade 9 student" },
  { v: "NEET",  l: "Qualified without preparation / extra coaching, first attempt" },
  { v: "JEE 88%", l: "Percentile without coaching" },
  { v: "1,500+", l: "Gold medals in SOF Olympiads over the last 10 years (800+ by primary students)" },
  { v: "Bronze", l: "PhysicsBowl international bronze" },
  { v: "Rank 1", l: "Twice International Rank 1 in SOF Olympiads" },
];

const ADMITS = [
  { u: "Rose-Hulman Institute of Technology · USA", tag: "Noblitt Scholar" },
  { u: "Lancaster University · UK",                  tag: "Physics & Astrophysics Scholarship" },
  { u: "University of Nottingham · UK",              tag: "100% Scholarship — Biotechnology" },
  { u: "Multiple USA & UK universities",             tag: "50%–100% scholarships across cohorts" },
];

export default function NTP() {
  return (
    <div data-testid="ntp-page">
      <PageHero
        n="I"
        label="NextGen Training Program"
        lines={["Great results are built", "by great learning systems."]}
        intro="Not by chance. Not by individual talent. The NextGen Training Program (NTP) prepares Grades 6–12 for the world's most prestigious STEM competitions and international university admissions."
      />

      {/* Why problem-solving is the currency of global admissions — placed right under the hero */}
      <section className="border-t border-white/10 bg-[#070d18]" data-testid="ntp-why-problem-solving-section">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-14 md:py-20">
          <Reveal>
            <div className="rounded-2xl border-l-4 border-accent border-t border-r border-b border-white/10 bg-[#050B14] p-7 md:p-10 max-w-5xl" data-testid="ntp-why-problem-solving">
              <div className="font-mono-label text-[11px] tracking-label uppercase text-accent">Why Problem-Solving Is Non-Negotiable</div>
              <p className="text-white/85 leading-relaxed mt-4 text-lg">
                Every leading national and international university admissions test is <span className="text-white font-semibold">built entirely around problem-solving</span> in the STEM subjects — <span className="text-white">Physics, Chemistry, Biology and Mathematics</span>. Their goal is to measure a student's <span className="text-accent">ability to solve problems</span>, not to reproduce facts.
              </p>
              <p className="text-white/75 leading-relaxed mt-4">
                The world's top universities also give strong preference to students who qualify high-level academic examinations — <span className="text-white">IPhO, IChO, IBO, IMO, AP, AMC, BPhO</span> and similar. Even <span className="text-white">NEET and JEE</span> are, at their core, completely problem-based exams.
              </p>
              <p className="text-white/75 leading-relaxed mt-4">
                A student who wants to compete for these opportunities must therefore be <span className="text-white">trained in a very high level of problem solving</span> — and that is exactly what NTP is designed to build.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Program Overview */}
      <section id="overview" className="border-t border-white/10 scroll-mt-24" data-testid="ntp-overview">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-20 md:py-28 grid md:grid-cols-12 gap-12 items-start">
          <Reveal className="md:col-span-7">
            <SectionLabel n="A">Program Overview</SectionLabel>
            <h2 className="font-display text-3xl md:text-5xl text-white mt-6 tracking-tight leading-tight">
              We don't just prepare students for exams. We prepare them for research, innovation and leadership.
            </h2>
            <div className="mt-8 space-y-5 text-white/75 leading-relaxed">
              <p>The NextGen Training Program (NTP) prepares students from <span className="text-accent font-semibold">Grades 6 to 12</span> for some of the world's most prestigious STEM competitions and national & international examinations — including <span className="text-white">NEET, JEE</span>, the <span className="text-white">British Physics Olympiad (BPhO), American Mathematics Competitions (AMC), Advanced Placement (AP), BIOlympiad, PhysicsBowl</span> and other national and global academic challenges.</p>
              <p>Unlike conventional coaching programmes that focus only on examination technique, NTP develops the <span className="text-white">conceptual understanding, analytical thinking, scientific reasoning and problem-solving skills</span> required to excel in these highly competitive examinations.</p>
              <p>Teaching is built on our <span className="text-accent">PBL-TDLE</span> model — Problem-Based Learning through Teaching by Demonstrations and Learning by Experiments — where students first develop concepts through demonstrations and experiments before applying them to challenging, real-life open-ended problems.</p>
              <p>Alongside preparation for international examinations, NTP supports students in building strong academic profiles for admission to <span className="text-white">leading universities in India and abroad</span>.</p>
            </div>
          </Reveal>

          <Reveal delay={0.08} className="md:col-span-5">
            <div className="rounded-2xl border border-white/10 p-8 md:p-10 text-white" style={{ background: "linear-gradient(120deg,#0B1B30 0%, #1B4A7A 55%, #2E6BB0 100%)" }}>
              <div className="flex items-center gap-2 font-mono-label text-[11px] tracking-label uppercase text-onimg">
                <Sparkles className="w-4 h-4" /> The NTP Promise
              </div>
              <p className="font-display text-2xl md:text-3xl mt-4 leading-snug text-onimg">
                From classroom learning to global universities. From research projects to scholarships.
              </p>
              <ul className="mt-8 space-y-3">
                {["Problem solvers", "Innovators", "Researchers", "Future global leaders"].map((s) => (
                  <li key={s} className="flex items-center gap-3" style={{ color: "#ffffff" }}>
                    <CheckCircle2 className="w-5 h-5" style={{ color: "#ffffff" }} /> {s}
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>
      </section>

      {/* NTP Edges */}
      <section className="border-t border-white/10 bg-[#070d18]" data-testid="ntp-edges">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-20 md:py-28">
          <SectionLabel n="B">Why Families Choose NTP</SectionLabel>
          <h2 className="font-display text-3xl md:text-5xl text-white mt-6 tracking-tight leading-tight max-w-4xl font-light">
            Expert mentorship. Personalised strategy. Global exposure. Proven results.
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-4 mt-12">
            {NTP_EDGES.map((e, i) => {
              const Icon = e.icon;
              return (
                <Reveal key={e.t} delay={i * 0.06} className="border border-white/10 rounded-2xl p-6 bg-[#050B14] hover:border-white/25 transition-colors" data-testid={`ntp-edge-${i}`}>
                  <div className="w-11 h-11 rounded-lg flex items-center justify-center border" style={{ borderColor: e.hex, backgroundColor: `${e.hex}22` }}>
                    <Icon className="w-6 h-6" style={{ color: e.hex }} />
                  </div>
                  <h3 className="font-display text-lg md:text-xl text-white mt-5 tracking-tight leading-tight">{e.t}</h3>
                  <p className="text-white/60 leading-relaxed mt-3 text-sm">{e.d}</p>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Mentors + Philosophy */}
      <section className="border-t border-white/10" data-testid="ntp-mentors">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-20 md:py-28 grid md:grid-cols-12 gap-12 items-start">
          <Reveal className="md:col-span-6">
            <SectionLabel n="C">Guided by Researchers & International Mentors</SectionLabel>
            <h2 className="font-display text-3xl md:text-4xl text-white mt-6 tracking-tight leading-tight font-light">
              Mentored by an experienced team of scientists, researchers and educators.
            </h2>
            <p className="text-white/70 leading-relaxed mt-6">
              Our mentors bring experience from <span className="text-white">TIFR, the IITs, international research laboratories and top global universities</span>. They guide students in subject mastery, competition strategy, research aptitude and higher-order thinking.
            </p>
            <p className="text-white/60 leading-relaxed mt-4">
              The focus is not only on gaining admission, but on helping students become future <span className="text-accent">researchers, innovators, entrepreneurs and global leaders</span>.
            </p>
          </Reveal>
          <Reveal delay={0.08} className="md:col-span-6">
            <div className="border border-white/10 rounded-2xl p-8">
              <div className="font-mono-label text-[11px] tracking-label uppercase text-accent">Our Philosophy — Six Pillars</div>
              <p className="text-white/70 leading-relaxed mt-4">
                Exceptional university admissions are not achieved through application assistance alone. They are built over several years through:
              </p>
              <ul className="mt-6 space-y-3">
                {PHILOSOPHY.map((p) => (
                  <li key={p} className="flex items-start gap-3 text-white/80 text-sm leading-relaxed">
                    <CheckCircle2 className="w-4 h-4 text-c-teal shrink-0 mt-0.5" /> {p}
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Proven excellence — 15 tile grid from the achievements image */}
      <section className="border-t border-white/10 bg-[#070d18]" data-testid="ntp-highlights">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-20 md:py-28">
          <div className="flex items-center gap-3">
            <Award className="w-5 h-5 text-accent" />
            <SectionLabel n="D">International Achievements by NTP</SectionLabel>
          </div>
          <h2 className="font-display text-3xl md:text-5xl text-white mt-6 tracking-tight leading-tight max-w-4xl font-light">
            Proven excellence. Global recognition. Limitless opportunities.
          </h2>
          <p className="text-white/60 text-lg leading-relaxed mt-6 max-w-3xl">
            A snapshot of what NTP students at The Creator International School have achieved on the world stage.
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 mt-12">
            {HIGHLIGHTS.map((h, i) => {
              const cols = ["#2E6BB0", "#F0A831", "#1E9E8A", "#8B5CF6", "#E14C7A"];
              const hex = cols[i % cols.length];
              return (
                <Reveal key={i} delay={(i % 5) * 0.05} className="border border-white/10 rounded-2xl p-5 bg-[#050B14] hover:border-white/25 transition-colors" data-testid={`ntp-highlight-${i}`}>
                  <div className="font-display text-3xl md:text-4xl tracking-tight leading-none" style={{ color: hex }}>{h.v}</div>
                  <p className="text-white/70 text-xs leading-relaxed mt-3">{h.l}</p>
                </Reveal>
              );
            })}
          </div>
          <p className="font-mono-label text-[11px] tracking-label uppercase text-white/55 mt-10">
            ★ Our students continue to achieve success in global problem-solving exams of high repute — making us proud on the world stage.
          </p>
        </div>
      </section>

      {/* Proven success — university admits list */}
      <section className="border-t border-white/10" data-testid="ntp-admits">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-20 md:py-28">
          <SectionLabel n="E">Proven Success — Universities Our Students Attend</SectionLabel>
          <div className="grid md:grid-cols-2 gap-6 mt-12">
            {ADMITS.map((a, i) => (
              <Reveal key={a.u} delay={i * 0.06} className="border border-white/10 rounded-2xl p-8 flex items-start gap-4">
                <GraduationCap className="w-8 h-8 text-c-blue shrink-0" />
                <div>
                  <h3 className="font-display text-xl md:text-2xl text-white tracking-tight leading-snug">{a.u}</h3>
                  <p className="font-mono-label text-[11px] tracking-label uppercase text-accent mt-3">{a.tag}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* International Admissions — how NTP + PBL-TDLE make international admissions easier */}
      <section id="admissions" className="border-t border-white/10 bg-[#070d18] scroll-mt-24" data-testid="ntp-admissions">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-20 md:py-28">
          <SectionLabel n="F">International Admissions</SectionLabel>
          <h2 className="font-display text-3xl md:text-5xl text-white mt-6 tracking-tight leading-tight max-w-4xl">
            How NTP and PBL-TDLE make international admissions easier.
          </h2>

          <div className="grid md:grid-cols-12 gap-12 mt-14 items-start">
            <Reveal className="md:col-span-6">
              <p className="text-white/75 leading-relaxed">
                International universities — especially those known for science and mathematics — look <span className="text-white font-semibold">beyond examination marks</span>. They seek students who can apply concepts, solve unfamiliar problems, conduct investigations, think independently, and demonstrate genuine interest through research, projects and academic competitions.
              </p>
              <p className="text-white/65 leading-relaxed mt-4">
                THECREATOR Education prepares students for these expectations through its <span className="text-accent">PBL-TDLE</span> framework. Students develop concepts through demonstrations, experiments, investigation and verification — and then apply them to real-life, open-ended problems. This develops the scientific thinking, analytical ability and problem-solving skills required by leading universities.
              </p>
              <p className="text-white/65 leading-relaxed mt-4">
                The <span className="text-white">NextGen Training Program (NTP)</span> builds upon this foundation with structured, long-term support that turns capable students — even those from smaller towns — into globally competitive applicants.
              </p>
            </Reveal>

            <Reveal delay={0.08} className="md:col-span-6">
              <div className="border border-white/10 rounded-2xl p-8">
                <div className="font-mono-label text-[11px] tracking-label uppercase text-accent">What NTP Provides</div>
                <ul className="mt-6 space-y-3">
                  {[
                    "Advanced preparation in science and mathematics",
                    "Training for international Olympiads and competitive examinations",
                    "Research projects guided by scientists and researchers",
                    "Continuous problem-solving practice and assessment",
                    "Academic and career mentoring from an early stage",
                    "Support for university selection, applications, scholarships and financial aid",
                    "Long-term development of each student's academic and research profile",
                  ].map((b) => (
                    <li key={b} className="flex items-start gap-3 text-white/80 text-sm leading-relaxed">
                      <CheckCircle2 className="w-4 h-4 text-c-teal shrink-0 mt-0.5" /> {b}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          </div>

          {/* First Two 12th Batches banner removed — university crests below already carry the same info */}

          {/* Closing manifesto */}
          <div className="mt-14 rounded-2xl p-8 md:p-10 text-white" style={{ background: "linear-gradient(120deg,#0B1B30 0%, #1B4A7A 55%, #2E6BB0 100%)" }} data-testid="ntp-admissions-tagline">
            <p className="font-mono-label text-[11px] tracking-label uppercase" style={{ color: "#ffffff" }}>The NTP Vision</p>
            <p className="font-display text-2xl md:text-4xl mt-4 leading-snug" style={{ color: "#ffffff" }}>
              Inspiring curiosity. Building confidence. Shaping futures.
            </p>
            <p className="font-display text-xl md:text-2xl mt-2 leading-snug" style={{ color: "rgba(255,255,255,0.92)" }}>
              Problem solvers today. Innovators tomorrow.
            </p>
            <p className="leading-relaxed mt-6 max-w-4xl" style={{ color: "rgba(255,255,255,0.88)" }}>
              The goal of NTP and THECREATOR Education is to develop globally competent scientists, mathematicians, researchers and innovators — students who can learn from the world's leading institutions, build international collaborations, and use their knowledge to solve important problems for India and society.
            </p>
          </div>
        </div>
      </section>

      {/* NTP Pillars (retained) */}
      <section className="border-t border-white/10 bg-[#070d18]">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-20 md:py-28 grid md:grid-cols-2 gap-px bg-white/10">
          {PILLARS.map((s, i) => (
            <Reveal key={s.n} delay={(i % 2) * 0.1} className="bg-[#070d18] p-8 md:p-12">
              <SectionLabel n={s.n}>Pillar</SectionLabel>
              <h3 className="font-display text-2xl md:text-3xl text-white mt-6 tracking-tight leading-tight font-light">{s.t}</h3>
              <p className="text-white/60 leading-relaxed mt-4">{s.d}</p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Exam prep — badges + Program Structure (CAPS · DPST · SPAD) */}
      <section id="exams" className="border-t border-white/10 scroll-mt-24" data-testid="ntp-exams">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-20 md:py-28">
          <SectionLabel n="G">Advance Problem Solving Training</SectionLabel>
          <h2 className="font-display text-3xl md:text-5xl text-white mt-8 tracking-tight leading-tight font-light max-w-3xl">
            Preparation for the world's most competitive examinations.
          </h2>
          <div className="flex flex-wrap gap-3 mt-10">
            {EXAM_BADGES.map((b, i) => (
              <Reveal key={b} delay={i * 0.04}>
                <span className="inline-flex items-center border border-white/20 px-6 py-3 font-mono-label text-sm tracking-label uppercase text-white hover:bg-accent hover:text-[#050B14] hover:border-accent transition-colors cursor-default">
                  {b}
                </span>
              </Reveal>
            ))}
          </div>

          {/* Program Structure */}
          <div className="mt-20" data-testid="ntp-program-structure">
            <div className="font-mono-label text-[11px] tracking-label uppercase text-accent">Program Structure</div>
            <h3 className="font-display text-3xl md:text-4xl text-white mt-4 tracking-tight leading-tight max-w-4xl">
              NTP follows a systematic three-stage daily learning cycle.
            </h3>
            <p className="text-white/75 leading-relaxed mt-4 max-w-3xl">
              From concepts to solutions — every day, every step.
            </p>

            {/* Infographic + live class slider — side by side */}
            <div className="mt-10 grid lg:grid-cols-2 gap-8 items-start">
              <Reveal delay={0.06}>
                <div className="rounded-2xl overflow-hidden border border-white/10" data-testid="ntp-cycle-infographic">
                  <img
                    src="https://customer-assets-rejwkqb3.emergentagent.net/job_creator-stem-schools/artifacts/coexjrjv_image.png"
                    alt="From Concepts to Solutions — The NTP Daily Learning Cycle · CAPS → DPST → SPAD"
                    loading="lazy"
                    className="w-full h-auto block"
                  />
                </div>
              </Reveal>
              <Reveal delay={0.07}>
                <NtpLiveSlider />
              </Reveal>
            </div>

            {/* Evening academic sessions — purpose and importance */}
            <Reveal delay={0.07}>
              <div className="mt-10 grid md:grid-cols-12 gap-8 items-start rounded-2xl border border-white/10 p-8 md:p-10 bg-[#050B14]" data-testid="ntp-evening-sessions">
                <div className="md:col-span-4">
                  <div className="font-mono-label text-[11px] tracking-label uppercase text-accent">Evening Academic Sessions</div>
                  <h4 className="font-display text-2xl md:text-3xl text-white mt-3 tracking-tight leading-tight">
                    Conducted <span className="text-accent">after school hours</span> by research-educators.
                  </h4>
                  <div className="mt-5 inline-flex items-center gap-3 border border-white/15 rounded-full px-4 py-2">
                    <span className="w-2 h-2 rounded-full bg-accent" />
                    <span className="font-mono-label text-[11px] tracking-label uppercase text-white/80">Live · Evening · Small cohorts</span>
                  </div>
                </div>
                <div className="md:col-span-8">
                  <p className="text-white/75 leading-relaxed">
                    NTP academic sessions are held in the <span className="text-white font-semibold">evening, after regular school hours</span>, and are conducted live by <span className="text-white font-semibold">research-educators</span> — subject specialists with active research backgrounds in Physics, Chemistry, Biology and Mathematics.
                  </p>
                  <p className="text-white/70 leading-relaxed mt-4">
                    The purpose is deliberate. Mornings are reserved for regular school academics; evenings are dedicated to the <span className="text-white">deeper, competition-grade problem solving</span> that NTP demands. This separation lets students protect their school routine while still training rigorously for AP, Olympiads, AMC, BPhO, JEE, NEET and international university admissions.
                  </p>
                  <ul className="mt-5 grid sm:grid-cols-2 gap-2 text-white/70 text-sm leading-relaxed">
                    <li className="flex items-start gap-2"><span className="text-accent mt-1">•</span> Taught by research-educators, not exam coaches</li>
                    <li className="flex items-start gap-2"><span className="text-accent mt-1">•</span> Doesn't disturb school timetable or homework</li>
                    <li className="flex items-start gap-2"><span className="text-accent mt-1">•</span> Small cohorts — live doubt clearing every day</li>
                    <li className="flex items-start gap-2"><span className="text-accent mt-1">•</span> Continuous mentorship, not one-off workshops</li>
                  </ul>
                </div>
              </div>
            </Reveal>

            {/* Session structure — how a daily NTP session actually runs */}
            <Reveal delay={0.08}>
              <div className="mt-8 rounded-2xl border border-white/10 p-8 md:p-10" data-testid="ntp-session-structure">
                <div className="font-mono-label text-[11px] tracking-label uppercase text-accent">How a Daily NTP Session Runs</div>
                <p className="text-white/80 text-lg leading-relaxed mt-4 max-w-4xl">
                  Every daily academic session in NTP is built around <span className="text-white font-semibold">three integrated components</span> — <span className="text-c-blue font-semibold">CAPS</span>, <span className="text-c-teal font-semibold">DPST</span> and <span className="text-c-violet font-semibold">SPAD</span> — running back-to-back in a single sitting.
                </p>

                <div className="grid md:grid-cols-2 gap-4 mt-8">
                  <div className="rounded-xl p-6 border border-white/10 bg-[#050B14]">
                    <div className="font-mono-label text-[11px] tracking-label uppercase text-c-blue">Foundation Group</div>
                    <div className="font-display text-3xl md:text-4xl text-white mt-2 tracking-tight">1 hour</div>
                    <p className="text-white/60 text-sm leading-relaxed mt-3">A focused single-hour daily cycle covering all three components.</p>
                  </div>
                  <div className="rounded-xl p-6 border border-white/10 bg-[#050B14]">
                    <div className="font-mono-label text-[11px] tracking-label uppercase text-c-amber">Intermediate & Advanced Group</div>
                    <div className="font-display text-3xl md:text-4xl text-white mt-2 tracking-tight">2 hours</div>
                    <p className="text-white/60 text-sm leading-relaxed mt-3">A deeper, two-hour daily cycle — more problem depth, richer discussion.</p>
                  </div>
                </div>

                <div className="mt-8">
                  <div className="font-mono-label text-[11px] tracking-label uppercase text-white/60">Time Split · Any Session</div>
                  <div className="mt-4 h-12 rounded-full overflow-hidden border border-white/10 flex" data-testid="ntp-time-bar">
                    <div className="flex items-center justify-center text-white font-mono-label text-[11px] tracking-label uppercase" style={{ width: "50%", backgroundColor: "#2E6BB0" }}>50% · CAPS</div>
                    <div className="flex items-center justify-center text-white font-mono-label text-[11px] tracking-label uppercase" style={{ width: "25%", backgroundColor: "#1E9E8A" }}>25% · DPST</div>
                    <div className="flex items-center justify-center text-white font-mono-label text-[11px] tracking-label uppercase" style={{ width: "25%", backgroundColor: "#8B5CF6" }}>25% · SPAD</div>
                  </div>
                  <p className="text-white/55 text-sm leading-relaxed mt-3">
                    50% of the session is devoted to <span className="text-white">CAPS</span> — concept application and problem solving. The remaining 50% is split evenly between <span className="text-white">DPST</span> and <span className="text-white">SPAD</span>.
                  </p>
                </div>

                <ol className="mt-8 space-y-3">
                  {[
                    { hex: "#2E6BB0", t: "CAPS — Live concept application",   d: "Expert researcher demonstrates how multiple concepts combine to solve Cambridge, AP, Olympiad, JEE and NEET-grade problems." },
                    { hex: "#1E9E8A", t: "DPST — 20-minute online test",       d: "The moment CAPS ends, an online test is auto-generated. Every student attempts it live during the same session — no take-home lag." },
                    { hex: "#8B5CF6", t: "SPAD — Presentations, discussion & doubt clearing", d: "Immediately after DPST, students present solutions, discuss alternate approaches and clear every conceptual doubt with the mentor before leaving the room." },
                  ].map((r, i) => (
                    <li key={r.t} className="flex items-start gap-4 border border-white/10 rounded-xl p-5">
                      <span className="w-9 h-9 rounded-full flex items-center justify-center font-display text-lg shrink-0" style={{ backgroundColor: `${r.hex}22`, color: r.hex, border: `1px solid ${r.hex}` }}>{i + 1}</span>
                      <div>
                        <div className="font-display text-lg text-white leading-snug">{r.t}</div>
                        <p className="text-white/60 leading-relaxed mt-1 text-sm">{r.d}</p>
                      </div>
                    </li>
                  ))}
                </ol>

                <p className="text-white/55 text-sm leading-relaxed mt-6">
                  ★ Every concept is applied, every solve is tested, every doubt is closed — in the same daily sitting.
                </p>
              </div>
            </Reveal>

            {/* Three-stage cards */}
            <div className="grid md:grid-cols-3 gap-6 mt-6">
              {[
                {
                  key: "caps",
                  code: "01",
                  hex: "#2E6BB0",
                  short: "CAPS",
                  t: "Concept Application & Problem Solving",
                  d: "Students learn how concepts are applied in solving challenging problems from Cambridge, AP, Olympiads, JEE, NEET and other international examinations.",
                  by: "Done by expert researcher",
                },
                {
                  key: "dpst",
                  code: "02",
                  hex: "#1E9E8A",
                  short: "DPST",
                  t: "Daily Problem Solving Tests",
                  d: "Regular practice sessions designed to improve speed, accuracy, confidence and examination readiness.",
                  by: "Daily practice · Builds accuracy · Improves speed · Tracks progress",
                },
                {
                  key: "spad",
                  code: "03",
                  hex: "#8B5CF6",
                  short: "SPAD",
                  t: "Solution Presentations, Analytical Discussion & Doubt Clearing",
                  d: "Students present solutions, participate in analytical discussions, evaluate different approaches, and clear conceptual doubts under expert guidance.",
                  by: "Guided & done by research expert",
                },
              ].map((s, i) => (
                <Reveal key={s.key} delay={i * 0.06} className="border border-white/10 rounded-2xl p-8 bg-[#050B14] flex flex-col" data-testid={`ntp-stage-${s.key}`}>
                  <div className="flex items-baseline gap-4">
                    <span className="font-display text-4xl" style={{ color: s.hex }}>{s.code}</span>
                    <span className="font-display text-3xl text-white tracking-tight">{s.short}</span>
                  </div>
                  <h4 className="font-display text-lg text-white mt-4 tracking-tight leading-snug">{s.t}</h4>
                  <p className="text-white/65 leading-relaxed mt-4 text-sm flex-1">{s.d}</p>
                  <div className="mt-5 pt-4 border-t border-white/10 font-mono-label text-[10px] tracking-label uppercase" style={{ color: s.hex }}>{s.by}</div>
                </Reveal>
              ))}
            </div>

            {/* Monthly Physical Mentoring */}
            <Reveal delay={0.1}>
              <div className="mt-10 rounded-2xl overflow-hidden border border-white/10" style={{ background: "linear-gradient(120deg,#0B1B30 0%, #1B4A7A 55%, #2E6BB0 100%)" }} data-testid="ntp-mentoring">
                <div className="p-8 md:p-10">
                  <div className="font-mono-label text-[11px] tracking-label uppercase" style={{ color: "#F0A831" }}>Monthly Physical Mentoring Sessions</div>
                  <h4 className="font-display text-2xl md:text-3xl mt-4 leading-tight" style={{ color: "#ffffff" }}>
                    Advanced-group students meet mentors in person — every month.
                  </h4>
                  <p className="mt-4 max-w-3xl leading-relaxed" style={{ color: "rgba(255,255,255,0.85)" }}>
                    Exclusive in-person mentoring sessions covering the moves that turn strong students into globally competitive candidates.
                  </p>
                  <ul className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-8">
                    {[
                      "International university admissions",
                      "Career planning & pathway guidance",
                      "Research and innovation opportunities",
                      "Scholarship strategies",
                      "Academic goal setting",
                      "Leadership & personal development",
                    ].map((m) => (
                      <li key={m} className="flex items-start gap-2 rounded-lg px-4 py-3" style={{ backgroundColor: "rgba(255,255,255,0.08)", color: "#ffffff" }}>
                        <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" style={{ color: "#F0A831" }} />
                        <span className="text-sm leading-snug">{m}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </Reveal>

            <p className="text-white/60 leading-relaxed mt-8 max-w-3xl">
              This integrated structure ensures <span className="text-white">continuous development from concepts to solutions</span> — and from learners to problem solvers.
            </p>
          </div>
        </div>
      </section>

      {/* International Competitions — the achievements gallery from the source website */}
      <section id="competitions" className="border-t border-white/10 scroll-mt-24" data-testid="ntp-competitions">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-20 md:py-28">
          <SectionLabel n="H">International Competitions</SectionLabel>
          <h2 className="font-display text-3xl md:text-5xl text-white mt-6 tracking-tight leading-tight max-w-4xl font-light">
            Global recognition earned on the world's toughest STEM stages.
          </h2>
          <p className="text-white/60 text-lg leading-relaxed mt-6 max-w-3xl">
            From BPhO and PhysicsBowl to AMC, AP and SOF Olympiads — every one of these recognitions was earned by an NTP cohort, in the very first year of participation.
          </p>

          {/* Competition-family cards */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mt-14">
            {[
              { key: "bpho",   fam: "BPhO · United Kingdom",       t: "British Physics Olympiad",               d: "All participating students received global awards; organiser's remark cited 'academic standards at their peak'.", tags: ["Junior · Global Gold", "Senior · International Medal"] },
              { key: "amc",    fam: "AMC · United States",         t: "American Mathematics Competitions",       d: "8 out of 13 students earned the Outstanding Achievement Award — in the very first year of participation.",     tags: ["AMC 10", "AMC 12", "AIME-qualifying"] },
              { key: "ap",     fam: "AP · College Board USA",      t: "AP Scholar Award",                        d: "College Board recognition for exemplary college-level achievement — scoring 3+ on 3+ AP Exams.",             tags: ["AP Physics", "AP Scholar"] },
              { key: "pbowl",  fam: "PhysicsBowl · International", t: "PhysicsBowl · Bronze",                    d: "Vihaan Deshmukh (Grade 9) — international bronze, competing across older cohorts.",                          tags: ["Grade 9", "Bronze 2026"] },
              { key: "sof",    fam: "SOF Olympiads",               t: "1,500+ Gold Medals · Decade of Gold",     d: "10 years of consecutive gold; twice International Rank 1; 2025-26 alone: 29 IEO + 31 IGKO golds.",           tags: ["IEO", "IGKO", "IMO", "NSO"] },
              { key: "bio",    fam: "BIOlympiad · International",  t: "BIOlympiad Medal by Grade 9",             d: "International medal earned by a Grade-9 NTP student — reflecting deep conceptual biology preparation.",     tags: ["BIOlympiad", "Grade 9"] },
            ].map((c, i) => {
              const cols = ["#2E6BB0", "#F0A831", "#1E9E8A", "#8B5CF6", "#E14C7A", "#22C55E"];
              return (
                <Reveal key={c.key} delay={(i % 3) * 0.06} className="border border-white/10 rounded-2xl p-6 bg-[#050B14] hover:border-white/25 transition-colors flex flex-col" data-testid={`ntp-comp-${c.key}`}>
                  <div className="font-mono-label text-[11px] tracking-label uppercase" style={{ color: cols[i] }}>{c.fam}</div>
                  <h3 className="font-display text-xl md:text-2xl text-white mt-3 tracking-tight leading-snug">{c.t}</h3>
                  <p className="text-white/65 leading-relaxed mt-3 text-sm flex-1">{c.d}</p>
                  <div className="flex flex-wrap gap-2 mt-5">
                    {c.tags.map((t) => (
                      <span key={t} className="font-mono-label text-[10px] tracking-label uppercase text-white/70 border border-white/15 rounded-full px-3 py-1">{t}</span>
                    ))}
                  </div>
                </Reveal>
              );
            })}
          </div>

          {/* Poster gallery — ALL posters from thecreatoreducation.com/international-achievements */}
          <div className="mt-14">
            <div className="flex items-center gap-3 mb-8">
              <Award className="w-5 h-5 text-accent" />
              <span className="font-mono-label text-[11px] tracking-label uppercase text-white/60">Every Poster · Every Recognition</span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="ntp-comp-gallery">
              {[
                { u: "bpho---new-AZxp2Vt2FUL5Cywt.png",             l: "BPhO · JPC 2026 · All Participants Awarded" },
                { u: "pbho-organizer-comments-RqhbVKzREi7Vdqr6.png",l: "BPhO Organizer's Remark for The Creator" },
                { u: "ap-award-5CHpMe6MOZLT2Mnb.png",               l: "AP Scholar Award · College Board" },
                { u: "8-13-amc-zIj3hYZzvVJ9FKrx.png",               l: "AMC 10 & 12 · 8 of 13 Outstanding" },
                { u: "physcis-bowl-B5gbO2KzGCkUcqTw.png",           l: "PhysicsBowl 2026 · Bronze · Vihaan Deshmukh" },
                { u: "gold-medals-z7FDQ6GJdgYNAnYG.png",            l: "A Decade of Gold · SOF Olympiads" },
                { u: "noblitt-pnKbDunerXX13CJP.png",                l: "Noblitt Scholar · Rose-Hulman IT · Ikhbaat" },
                { u: "ap-ikhbaat-3xEFduh51Za2NY2m.jpg",             l: "AP Award · Physics · Ikhbaat" },
                { u: "ikhbaat-W989AStyJ3FpJIed.png",                l: "Ikhbaat · Awards Overview" },
                { u: "kshitij-vF8PLP24JdH22Q3x.png",                l: "Lancaster · Physics with Astrophysics · Kshitij Agarwal" },
                { u: "nottingham-masira-CWSC0zMIlrg9Qo9X.png",      l: "Nottingham · Biotechnology · Masira Firdose" },
                { u: "vihaan-sFuDj9stjj33WxrH.png",                 l: "BPhO Junior · Global Gold · Vihaan" },
              ].map((p, i) => (
                <Reveal key={p.u} delay={(i % 4) * 0.05} className="group relative aspect-[3/4] overflow-hidden rounded-xl border border-white/10 bg-white" data-testid={`ntp-poster-${i}`}>
                  <img
                    src={`https://assets.zyrosite.com/cdn-cgi/image/format=auto,w=800,fit=crop/OyPMhZFo366K6YNr/${p.u}`}
                    alt={p.l}
                    loading="lazy"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-[#050B14]/95 to-transparent opacity-0 group-hover:opacity-100 transition-opacity p-3">
                    <span className="font-mono-label text-[10px] tracking-label uppercase text-onimg">{p.l}</span>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Where our students go — 4 destination universities with official crests */}
      <section id="destinations" className="border-t border-white/10 bg-[#070d18] scroll-mt-24" data-testid="ntp-destinations">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-20 md:py-28">
          <SectionLabel n="I">Where our students go</SectionLabel>
          <h2 className="font-display text-3xl md:text-5xl text-white mt-6 tracking-tight leading-tight max-w-4xl font-light">
            Four of the world universities where NTP students earned admits and scholarships.
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-12">
            {[
              {
                name: "University of Nottingham",
                country: "United Kingdom",
                admit: "B.S. Biotechnology · 100% Scholarship",
                crest: "https://upload.wikimedia.org/wikipedia/commons/thumb/c/c7/Shield_of_the_University_of_Nottingham.svg/330px-Shield_of_the_University_of_Nottingham.svg.png",
                site: "https://www.nottingham.ac.uk",
              },
              {
                name: "Rose-Hulman Institute of Technology",
                country: "United States",
                admit: "Noblitt Scholar · Physics + CS · Research Grant",
                crest: "https://upload.wikimedia.org/wikipedia/en/thumb/e/ed/Rose%E2%80%93Hulman_Institute_of_Technology_seal.svg/330px-Rose%E2%80%93Hulman_Institute_of_Technology_seal.svg.png",
                site: "https://www.rose-hulman.edu",
              },
              {
                name: "Lancaster University",
                country: "United Kingdom",
                admit: "BSc (Hons) Physics with Astrophysics · Full Scholarship",
                crest: "https://upload.wikimedia.org/wikipedia/commons/thumb/a/aa/University_of_Lancaster_shield.svg/330px-University_of_Lancaster_shield.svg.png",
                site: "https://www.lancaster.ac.uk",
              },
              {
                name: "University of Reading",
                country: "United Kingdom",
                admit: "Sciences · Undergraduate Admission",
                crest: "https://upload.wikimedia.org/wikipedia/commons/thumb/a/a0/Reading_shield.png/330px-Reading_shield.png",
                site: "https://www.reading.ac.uk",
              },
            ].map((u, i) => (
              <Reveal key={u.name} delay={i * 0.06} className="border border-white/10 rounded-2xl overflow-hidden bg-[#050B14] flex flex-col hover:border-accent transition-colors" data-testid={`ntp-destination-${i}`}>
                <a href={u.site} target="_blank" rel="noreferrer" className="block bg-white p-8 flex items-center justify-center min-h-[220px] border-b border-white/10">
                  <img src={u.crest} alt={`${u.name} — official crest`} loading="lazy" className="max-h-40 w-auto object-contain" />
                </a>
                <div className="p-6 flex-1 flex flex-col">
                  <div className="font-mono-label text-[11px] tracking-label uppercase text-accent">{u.country}</div>
                  <h3 className="font-display text-xl text-white mt-3 tracking-tight leading-snug">{u.name}</h3>
                  <p className="text-white/70 text-sm leading-relaxed mt-3 flex-1">{u.admit}</p>
                  <a href={u.site} target="_blank" rel="noreferrer" className="font-mono-label text-[10px] tracking-label uppercase text-white/50 hover:text-accent transition-colors mt-4 inline-flex items-center gap-2">
                    Visit site <ArrowUpRight className="w-3 h-3" />
                  </a>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTAs (retained) */}
      <section className="border-t border-white/10">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-24 grid md:grid-cols-2 gap-6">
          <Link to="/enroll" data-testid="ntp-bring-cta" className="group border border-white/15 p-10 hover:border-accent transition-colors flex items-center justify-between">
            <div>
              <span className="font-mono-label text-[11px] tracking-label uppercase text-accent">For Schools</span>
              <h3 className="font-display text-3xl md:text-4xl text-white mt-3 tracking-tight">Bring NTP to your school</h3>
            </div>
            <ArrowUpRight className="w-8 h-8 text-white/50 group-hover:text-accent group-hover:rotate-45 transition-all" />
          </Link>
          <Link to="/become-a-tutor" data-testid="ntp-tutor-cta" className="group border border-white/15 p-10 hover:border-accent transition-colors flex items-center justify-between">
            <div>
              <span className="font-mono-label text-[11px] tracking-label uppercase text-accent">For Educators</span>
              <h3 className="font-display text-3xl md:text-4xl text-white mt-3 tracking-tight">Become a tutor</h3>
            </div>
            <ArrowUpRight className="w-8 h-8 text-white/50 group-hover:text-accent group-hover:rotate-45 transition-all" />
          </Link>
        </div>
      </section>

      <CTASection />
    </div>
  );
}
